// frontend/src/components/book/atelier/store/bookReducer.js
//
// ETAT DU LIVRE — LA SEULE SOURCE DE VERITE de ce qui est enregistre ET
// annulable : le modele editable de chaque page + la couverture + l'historique.
// Reducer PUR (aucun reseau, aucune horloge : l'heure arrive dans chaque
// action), donc testable seul. Tout ce qui est selection, navigation, panneau
// ou fenetre ouverte vit AILLEURS (uiReducer.js) : un clic de selection ne
// doit jamais entrer dans l'historique ni declencher une sauvegarde.
//
// Principe de sauvegarde : le reducer ne sauvegarde rien. Il garde, pour
// chaque page, la signature de ce que le serveur possede (`saved`). Une page
// est "sale" quand la signature de son modele differe — c'est le moteur de
// sauvegarde (saveEngine.js) qui regarde et ecrit. Annuler/retablir ne font
// que remettre d'anciens modeles : le moteur se charge de les re-ecrire.

import { facingPageIndex } from '../../../../utils/pageParity';
import { findAtelierLayout } from '../atelierLayouts';
import { createEmptyModel, modelSignature, stableStringify } from './pageModel';

export const HISTORY_LIMIT = 50;
// Deux gestes de meme nature (glisser, frappe, choix de couleur) rapproches de
// moins que ca ne forment qu'UN pas d'historique.
export const COALESCE_WINDOW_MS = 1200;

export const BOOK_ACTIONS = {
  LOAD: 'book/load',
  EDIT_PAGE: 'book/editPage',
  EDIT_COVER: 'book/editCover',
  EDIT_PAGES: 'book/editPages',
  UNDO: 'book/undo',
  REDO: 'book/redo',
  MARK_SAVED: 'book/markSaved',
  SET_STATUS: 'book/setStatus',
  RESET_HISTORY: 'book/resetHistory'
};

export const createInitialBookState = () => ({
  loaded: false,
  pageCount: 0,
  defaultSlug: null,
  pages: {},
  cover: null,
  saved: { pages: {}, cover: null },
  history: { past: [], future: [] },
  lastEdit: null,
  // 'idle' | 'saving' | 'saved' | 'offline' — les trois seuls etats montres a
  // l'utilisateur (plus un etat initial muet).
  status: 'idle'
});

// --- signatures ----------------------------------------------------------

const signatureCache = new WeakMap();
export function pageSignature(model) {
  if (!model) return stableStringify({ k: 'blank' });
  let signature = signatureCache.get(model);
  if (signature === undefined) {
    signature = modelSignature(model);
    signatureCache.set(model, signature);
  }
  return signature;
}

export const coverSignature = (cover) => stableStringify(cover || null);

/** Unites a ecrire : pages dont la signature differe de celle du serveur, + couverture. */
export function listDirty(state) {
  const dirty = [];
  Object.keys(state.pages).forEach((key) => {
    const index = Number(key);
    // Une page hors du livre (le serveur a redimensionne le livre, ex. apres une
    // composition automatique) n'existe plus : rien a ecrire.
    if (index >= state.pageCount) return;
    const signature = pageSignature(state.pages[index]);
    if (signature !== state.saved.pages[index]) dirty.push({ unit: 'page', index, signature });
  });
  dirty.sort((a, b) => a.index - b.index);
  if (state.cover) {
    const signature = coverSignature(state.cover);
    if (signature !== state.saved.cover) dirty.push({ unit: 'cover', signature });
  }
  return dirty;
}

export const canUndo = (state) => state.history.past.length > 0;
export const canRedo = (state) => state.history.future.length > 0;

const defaultModels = new Map();
/** Modele d'une page, ou un brouillon vierge STABLE (meme objet d'un rendu a l'autre). */
export function getPageModel(state, index) {
  if (index == null) return null;
  if (state.pages[index]) return state.pages[index];
  const key = state.defaultSlug || '';
  if (!defaultModels.has(key)) defaultModels.set(key, createEmptyModel(state.defaultSlug));
  return defaultModels.get(key);
}

// --- doubles pages -------------------------------------------------------
// Une photo en double page est enregistree a l'IDENTIQUE sur les deux pages
// qui se font face (le rendu deduit la moitie a afficher de la parite — voir
// pageRenderer.js). Avant : mirrorSpread/releaseSpreadSibling, deux fonctions
// asynchrones greffees sur la sauvegarde. Maintenant : la page jumelle est un
// modele comme un autre, mis a jour DANS LA MEME action — donc dans le meme
// pas d'historique, et ecrite par le meme moteur.
function spreadSibling(pages, index, previous, next, pageCount, defaultSlug) {
  const twin = facingPageIndex(index);
  if (twin == null || twin >= pageCount) return null;
  const wasSpread = !previous.kind && previous.layoutSlug && findAtelierLayout(previous.layoutSlug)?.spread === true;
  const isSpread = !next.kind && next.layoutSlug && findAtelierLayout(next.layoutSlug)?.spread === true;
  if (isSpread) return { index: twin, model: { ...next } };
  // On quitte la double page : liberer la jumelle, sinon il resterait une
  // demi-photo orpheline a cote.
  if (wasSpread) return { index: twin, model: { ...createEmptyModel(defaultSlug), touched: true } };
  return null;
}

// --- historique ----------------------------------------------------------

const modelsEqual = (a, b) => stableStringify({ ...a, touched: null }) === stableStringify({ ...b, touched: null });

function pushEntry(state, entry, meta) {
  const { past } = state.history;
  const key = meta?.coalesceKey || null;
  const last = past[past.length - 1];
  const sameUnit = last && stableStringify(Object.keys(last.after.pages || {}).sort()) === stableStringify(Object.keys(entry.after.pages || {}).sort())
    && Boolean(last.after.cover) === Boolean(entry.after.cover);
  const mergeable = key && state.lastEdit && state.lastEdit.key === key
    && (meta.at - state.lastEdit.at) <= COALESCE_WINDOW_MS && sameUnit && !last.bulk;

  let nextPast;
  if (mergeable) {
    nextPast = [...past.slice(0, -1), { ...last, after: entry.after }];
  } else {
    nextPast = [...past, entry];
    if (nextPast.length > HISTORY_LIMIT) nextPast = nextPast.slice(nextPast.length - HISTORY_LIMIT);
  }
  return {
    history: { past: nextPast, future: [] },
    lastEdit: { key, at: meta?.at ?? 0, fast: Boolean(meta?.fast) }
  };
}

function applySide(state, side, replaceAll) {
  const pages = replaceAll ? { ...(side.pages || {}) } : { ...state.pages, ...(side.pages || {}) };
  // Annuler une composition automatique ne peut pas rendre au livre des pages
  // qu'il n'a plus (le serveur l'a peut-etre redimensionne entre-temps).
  Object.keys(pages).forEach((key) => { if (Number(key) >= state.pageCount) delete pages[key]; });
  return { ...state, pages, cover: side.cover !== undefined && side.cover !== null ? side.cover : state.cover };
}

// --- reducer -------------------------------------------------------------

export function bookReducer(state, action) {
  switch (action.type) {
    case BOOK_ACTIONS.LOAD: {
      const pages = action.pages || {};
      const savedPages = {};
      Object.keys(pages).forEach((key) => { savedPages[key] = pageSignature(pages[key]); });
      const next = {
        ...state,
        loaded: true,
        pageCount: action.pageCount,
        defaultSlug: action.defaultSlug ?? state.defaultSlug,
        pages,
        cover: action.cover !== undefined ? action.cover : state.cover,
        saved: { pages: savedPages, cover: coverSignature(action.cover !== undefined ? action.cover : state.cover) },
        lastEdit: null
      };
      if (action.step) {
        // Une generation automatique = UN SEUL pas : on revient d'un coup au
        // livre d'avant. Le serveur possede deja `pages` (saved le dit) ; si
        // on annule, les anciens modeles different de saved -> le moteur
        // les re-ecrit page par page.
        const entry = {
          label: action.step.label,
          key: null,
          bulk: true,
          replaceAll: true,
          at: action.step.at ?? 0,
          before: { pages: state.pages },
          after: { pages }
        };
        next.history = { past: [...state.history.past, entry].slice(-HISTORY_LIMIT), future: [] };
      } else if (!action.keepHistory) {
        next.history = { past: [], future: [] };
      }
      return next;
    }

    case BOOK_ACTIONS.EDIT_PAGE: {
      const { index, meta } = action;
      const previous = getPageModel(state, index);
      const patch = typeof action.patch === 'function' ? action.patch(previous) : action.patch;
      const next = { ...previous, ...patch, touched: true };
      if (modelsEqual(previous, next)) return state;

      const pages = { ...state.pages, [index]: next };
      const before = { pages: { [index]: previous } };
      const after = { pages: { [index]: next } };

      const sibling = spreadSibling(pages, index, previous, next, state.pageCount, state.defaultSlug);
      if (sibling) {
        before.pages[sibling.index] = getPageModel(state, sibling.index);
        after.pages[sibling.index] = sibling.model;
        pages[sibling.index] = sibling.model;
      }

      const entry = { label: meta?.label || '', key: meta?.coalesceKey || null, at: meta?.at ?? 0, before, after };
      return { ...state, pages, ...pushEntry(state, entry, meta) };
    }

    // Une modification appliquee a PLUSIEURS pages d'un coup (ex. "Remplir tous
    // les cadres") : UN seul pas d'historique, donc un seul "Annuler".
    // `updater(index, model)` renvoie le patch de cette page, ou null pour la
    // laisser telle quelle.
    case BOOK_ACTIONS.EDIT_PAGES: {
      const { meta } = action;
      const pages = { ...state.pages };
      const before = { pages: {} };
      const after = { pages: {} };
      Object.keys(state.pages).forEach((key) => {
        const index = Number(key);
        if (index >= state.pageCount) return;
        const previous = state.pages[index];
        const patch = action.updater(index, previous);
        if (!patch) return;
        const next = { ...previous, ...patch, touched: true };
        if (modelsEqual(previous, next)) return;
        before.pages[index] = previous;
        after.pages[index] = next;
        pages[index] = next;
      });
      if (Object.keys(after.pages).length === 0) return state;
      const entry = { label: meta?.label || '', key: null, at: meta?.at ?? 0, before, after };
      return { ...state, pages, ...pushEntry(state, entry, { ...meta, coalesceKey: null }) };
    }

    case BOOK_ACTIONS.EDIT_COVER: {
      const { meta } = action;
      const previous = state.cover || { title: '', overrides: {} };
      let next;
      if (typeof action.patch === 'function') {
        next = action.patch(previous);
      } else {
        const overrides = { ...(previous.overrides || {}), ...(action.patch.overrides || {}) };
        (action.patch.remove || []).forEach((key) => { delete overrides[key]; });
        next = {
          ...previous,
          ...(action.patch.title !== undefined ? { title: action.patch.title } : {}),
          overrides
        };
      }
      if (coverSignature(previous) === coverSignature(next)) return state;
      const entry = { label: meta?.label || '', key: meta?.coalesceKey || null, at: meta?.at ?? 0, before: { cover: previous }, after: { cover: next } };
      return { ...state, cover: next, ...pushEntry(state, entry, meta) };
    }

    case BOOK_ACTIONS.UNDO: {
      const { past, future } = state.history;
      if (past.length === 0) return state;
      const entry = past[past.length - 1];
      const restored = applySide(state, entry.before, entry.replaceAll);
      return { ...restored, history: { past: past.slice(0, -1), future: [...future, entry] }, lastEdit: null };
    }

    case BOOK_ACTIONS.REDO: {
      const { past, future } = state.history;
      if (future.length === 0) return state;
      const entry = future[future.length - 1];
      const restored = applySide(state, entry.after, entry.replaceAll);
      return { ...restored, history: { past: [...past, entry], future: future.slice(0, -1) }, lastEdit: null };
    }

    case BOOK_ACTIONS.MARK_SAVED: {
      if (action.unit === 'cover') {
        return { ...state, saved: { ...state.saved, cover: action.signature } };
      }
      return { ...state, saved: { ...state.saved, pages: { ...state.saved.pages, [action.index]: action.signature } } };
    }

    case BOOK_ACTIONS.SET_STATUS:
      return state.status === action.status ? state : { ...state, status: action.status };

    case BOOK_ACTIONS.RESET_HISTORY:
      return { ...state, history: { past: [], future: [] }, lastEdit: null };

    default:
      return state;
  }
}
